/**
 * SurveyResponsesPage.tsx
 *
 * Paginated list of responses for a single survey, with a detail drawer,
 * and a stats panel showing rating/NPS question breakdowns.
 */
import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn, DataTableAction } from '@/components/ui/DataTable';
import { Modal } from '@/components/ui/Modal';
import { Pagination } from '@/components/ui/Pagination';
import { Spinner } from '@/components/ui/Spinner';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';
import { Badge } from '@/components/ui/Badge';
import { Tabs } from '@/components/ui/Tabs';
import * as surveysSvc from '@/services/surveys.service';
import type {
  Survey,
  SurveyResponse,
  SurveyAnswer,
  QuestionRatingSummary,
} from '@/services/surveys.service';

const PER_PAGE = 20;

// ─── Response detail modal ─────────────────────────────────────────────────────

interface ResponseDetailProps {
  response: SurveyResponse;
  onClose: () => void;
}

function ResponseDetail({ response, onClose }: ResponseDetailProps) {
  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Response #${response.id}`}
      size="lg"
    >
      <div className="space-y-1 mb-4">
        <p className="text-sm text-slate-500">
          Submitted: {new Date(response.submittedAt).toLocaleString()}
        </p>
      </div>

      <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
        {response.answers.map((answer: SurveyAnswer, i: number) => (
          <div key={i} className="border border-slate-200 rounded-md p-3">
            <p className="text-xs font-medium text-slate-500 mb-1">
              {answer.questionLabel}
              {answer.questionType === 6 && (
                <Badge variant="info" className="ml-2">NPS</Badge>
              )}
              {answer.questionType === 5 && (
                <Badge variant="info" className="ml-2">Rating</Badge>
              )}
              {answer.questionType === 28 && (
                <Badge variant="info" className="ml-2">Acceptance</Badge>
              )}
            </p>
            <p className="text-sm text-slate-800">
              {answer.value !== null && answer.value !== '' ? answer.value : (
                <span className="italic text-slate-400">No answer</span>
              )}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-6 flex justify-end">
        <Button onClick={onClose}>Close</Button>
      </div>
    </Modal>
  );
}

// ─── Ratings stats panel ───────────────────────────────────────────────────────

interface RatingsSummaryProps {
  surveyId: number;
}

function RatingsSummary({ surveyId }: RatingsSummaryProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['survey-ratings', surveyId],
    queryFn: () => surveysSvc.getRatingsReport(surveyId, {}),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  if (!data || data.questions.length === 0) {
    return (
      <p className="text-sm text-slate-500 py-8 text-center">
        No rating or NPS questions in this survey.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      {data.questions.map((q: QuestionRatingSummary) => (
        <div key={q.questionId} className="border border-slate-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">{q.questionLabel}</h3>

          <MetricCardGrid columns={3} className="mb-4">
            <StatCard
              label="Average"
              value={q.average !== null ? q.average.toFixed(1) : '—'}
            />
            <StatCard label="Responses" value={String(q.count)} />
            {q.questionType === 6 && q.npsScore !== undefined && (
              <StatCard
                label="NPS Score"
                value={String(q.npsScore)}
                subtitle={
                  q.npsScore >= 50
                    ? 'Excellent'
                    : q.npsScore >= 0
                    ? 'Good'
                    : 'Needs work'
                }
              />
            )}
          </MetricCardGrid>

          {/* Distribution bar chart */}
          {Object.keys(q.distribution).length > 0 && (
            <div className="space-y-1.5">
              {Object.entries(q.distribution)
                .sort(([a], [b]) => Number(a) - Number(b))
                .map(([score, cnt]) => {
                  const pct = q.count > 0 ? Math.round((cnt / q.count) * 100) : 0;
                  return (
                    <div key={score} className="flex items-center gap-2 text-xs">
                      <span className="w-6 text-right text-slate-500 font-mono">{score}</span>
                      <div className="flex-1 bg-slate-100 rounded-full h-2">
                        <div
                          className="bg-blue-500 h-2 rounded-full"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="w-8 text-slate-600">{pct}%</span>
                    </div>
                  );
                })}
            </div>
          )}

          {/* NPS breakdown */}
          {q.questionType === 6 && (
            <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
              <div className="rounded-md bg-green-50 p-2">
                <p className="font-semibold text-green-700">{q.promoters ?? 0}%</p>
                <p className="text-green-600">Promoters</p>
              </div>
              <div className="rounded-md bg-yellow-50 p-2">
                <p className="font-semibold text-yellow-700">{q.passives ?? 0}%</p>
                <p className="text-yellow-600">Passives</p>
              </div>
              <div className="rounded-md bg-red-50 p-2">
                <p className="font-semibold text-red-700">{q.detractors ?? 0}%</p>
                <p className="text-red-600">Detractors</p>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function SurveyResponsesPage() {
  const { surveyId } = useParams<{ surveyId: string }>();
  const id = Number(surveyId);
  const [page, setPage] = useState(1);
  const [viewingResponse, setViewingResponse] = useState<SurveyResponse | null>(null);
  const [activeTab, setActiveTab] = useState<'responses' | 'stats'>('responses');

  const { data: survey } = useQuery<Survey>({
    queryKey: ['survey', id],
    queryFn: () => surveysSvc.getSurvey(id),
    select: (d) => d as unknown as Survey,
  });

  const { data: responsesPage, isLoading } = useQuery({
    queryKey: ['survey-responses', id, page],
    queryFn: () => surveysSvc.getResponses(id, { page, perPage: PER_PAGE }),
    enabled: activeTab === 'responses',
  });

  const columns: DataTableColumn<SurveyResponse>[] = [
    {
      key: 'id',
      header: '#',
      render: (row) => <span className="text-slate-500 font-mono text-xs">#{row.id}</span>,
    },
    {
      key: 'submittedAt',
      header: 'Submitted',
      render: (row) => (
        <span className="text-sm">{new Date(row.submittedAt).toLocaleString()}</span>
      ),
    },
    {
      key: 'answerCount',
      header: 'Answers',
      render: (row) => (
        <Badge variant="info">{row.answers.length}</Badge>
      ),
    },
  ];

  const rowActions: DataTableAction<SurveyResponse>[] = [
    {
      label: 'View',
      onClick: (row) => setViewingResponse(row),
    },
  ];

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title={survey?.title ?? 'Survey Responses'}
        subtitle={
          responsesPage
            ? `${responsesPage.meta.total} response${responsesPage.meta.total !== 1 ? 's' : ''}`
            : undefined
        }
        actions={
          <Link to="/surveys/all">
            <Button variant="ghost" leftIcon={<ArrowLeft className="h-4 w-4" />}>
              Back to Surveys
            </Button>
          </Link>
        }
      />

      <div className="flex-1 overflow-auto p-6">
        <Tabs
          tabs={[
            { id: 'responses', label: 'Responses', content: null },
            { id: 'stats', label: 'Stats & Ratings', content: null },
          ]}
          defaultTab={activeTab}
          onChange={(tab) => setActiveTab(tab as typeof activeTab)}
          className="mb-6"
        />

        {activeTab === 'responses' && (
          <>
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Spinner size="lg" />
              </div>
            ) : (
              <>
                <DataTable<SurveyResponse>
                  columns={columns}
                  data={responsesPage?.data ?? []}
                  keyField="id"
                  rowActions={rowActions}
                  emptyTitle="No responses yet."
                />
                {responsesPage && (responsesPage.meta.totalPages ?? 1) > 1 && (
                  <div className="mt-4 flex justify-center">
                    <Pagination
                      total={responsesPage.meta.total ?? responsesPage.data.length}
                      page={page}
                      pageSize={PER_PAGE}
                      onPageChange={setPage}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}

        {activeTab === 'stats' && <RatingsSummary surveyId={id} />}
      </div>

      {viewingResponse && (
        <ResponseDetail
          response={viewingResponse}
          onClose={() => setViewingResponse(null)}
        />
      )}
    </div>
  );
}
